import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import baseUrl from '../../api/baseUrl'
import type { LabOrderDetail } from './labOrderTypes'

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { message?: string } } }
  return e?.response?.data?.message || fallback
}

/**
 * The order's job card (requirement 9): the server's PDF in the CDCI
 * layout, the same one the lab receives. View it here, print it, save it, or
 * email it to the lab.
 */
export default function JobCardPanel({
  order,
  onUpdated,
}: {
  order: LabOrderDetail
  onUpdated?: (order: LabOrderDetail) => void
}) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [printWhenReady, setPrintWhenReady] = useState(false)
  const [error, setError] = useState('')
  const orderId = order._id
  // Refetch when the order changes (status, stages, due date…).
  const version = `${orderId}|${order.orderStatus}|${order.stageLog.length}|${order.dueDate}|${order.priority ?? ''}`
  const [loadedVersion, setLoadedVersion] = useState('')
  const fileName = `job-card-${(order.clinicCaseId || order.id).replace(/[^A-Za-z0-9-]+/g, '-')}.pdf`

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  async function ensurePdf() {
    if (url && loadedVersion === version) return url
    setLoading(true)
    setError('')
    try {
      const res = await baseUrl.get(`client/labs/orders/${encodeURIComponent(orderId)}/job-card.pdf`, {
        responseType: 'blob',
      })
      const next = URL.createObjectURL(res.data as Blob)
      setUrl(next)
      setLoadedVersion(version)
      return next
    } catch (err) {
      setError(errorText(err, 'Could not load the job card'))
      return null
    } finally {
      setLoading(false)
    }
  }

  async function view() {
    if (open) {
      setOpen(false)
      return
    }
    if (await ensurePdf()) setOpen(true)
  }

  async function print() {
    const href = await ensurePdf()
    if (!href) return
    const win = frameRef.current?.contentWindow
    if (open && win && href === url) {
      // Already showing this card: the frame won't fire load again.
      win.focus()
      win.print()
      return
    }
    setOpen(true)
    setPrintWhenReady(true)
  }

  function onFrameLoad() {
    if (!printWhenReady) return
    setPrintWhenReady(false)
    const win = frameRef.current?.contentWindow
    win?.focus()
    win?.print()
  }

  async function download() {
    const href = await ensurePdf()
    if (!href) return
    const a = document.createElement('a')
    a.href = href
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  async function email() {
    setSending(true)
    setError('')
    try {
      const res = await baseUrl.post(`client/labs/orders/${encodeURIComponent(orderId)}/send-job-card`)
      toast.success(res.data?.message || 'Job card sent')
      if (res.data?.data && onUpdated) onUpdated(res.data.data as LabOrderDetail)
    } catch (err) {
      setError(errorText(err, 'Could not send the job card'))
    } finally {
      setSending(false)
    }
  }

  const noEmail = !order.labEmail
  const btn =
    'rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50'

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Job card</h3>
          <p className="mt-0.5 text-xs text-slate-500">The card the lab works from — print it with the case or email it.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={view} disabled={loading} className={btn} aria-expanded={open}>
            {open ? 'Hide' : 'View'}
          </button>
          <button type="button" onClick={print} disabled={loading} className={btn}>
            Print
          </button>
          <button type="button" onClick={download} disabled={loading} className={btn}>
            Download PDF
          </button>
          <button
            type="button"
            onClick={email}
            disabled={sending || noEmail}
            title={noEmail ? 'Add an email for this lab under Partner labs first' : undefined}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Email to lab'}
          </button>
        </div>
      </div>
      {noEmail ? (
        <p className="mt-2 text-xs text-slate-500">
          {order.labName || 'This lab'} has no email address. Add one under Partner labs to email the card.
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      ) : null}
      {loading ? <p className="mt-3 text-sm text-slate-500">Loading job card…</p> : null}
      {open && url ? (
        <iframe
          ref={frameRef}
          src={url}
          title="Job card"
          onLoad={onFrameLoad}
          className="mt-3 h-[70vh] w-full rounded-lg border border-slate-200 dark:border-slate-700"
        />
      ) : null}
    </div>
  )
}
